import type { VideoEncoding } from 'types/videoTypes';
import { getImageLoader } from 'utils/imageLoader';
import { getFilename, normalizeBaseUrl } from '../../../utils/image';
import { FALLBACK_IMAGE_URL } from '../FallbackImage/FallbackImage';
import FoundationPlayerClient from './FoundationPlayer.client';
import styles from './FoundationPlayer.module.scss';
import type { FoundationPlayerProps } from './types';

function getVideoSourceUrl(encodings?: Array<VideoEncoding>): string | undefined {
  if (!encodings || encodings.length === 0) return undefined;

  const progressiveEncoding = encodings.find(
    (enc) => enc.value['encoding-format']?.value?.toLowerCase() === 'progressive'
  );
  if (progressiveEncoding) {
    return progressiveEncoding.value['content-url']?.value;
  }

  return encodings[0]?.value['content-url']?.value;
}

function getVideoMimeType(encodings?: Array<VideoEncoding>): string {
  if (!encodings || encodings.length === 0) return 'video/mp4';

  const progressiveEncoding = encodings.find(
    (enc) => enc.value['encoding-format']?.value?.toLowerCase() === 'progressive'
  );
  if (progressiveEncoding) {
    return 'video/mp4';
  }

  const format = encodings[0]?.value['encoding-format']?.value?.toLowerCase();
  switch (format) {
    case 'hls':
      return 'application/x-mpegURL';
    case 'dash':
      return 'application/dash+xml';
    default:
      return 'video/mp4';
  }
}

const FoundationPlayer = (props: FoundationPlayerProps) => {
  const { video } = props;

  const optimizedImg = video?.image?.baseUrl
    ? getImageLoader(
        normalizeBaseUrl(video.image.baseUrl),
        getFilename(video.fields.title, video.image.fields?.alt?.value),
        'c16_9'
      ).loader({
        src: normalizeBaseUrl(video?.image?.baseUrl),
        width: 1200,
      })
    : FALLBACK_IMAGE_URL;
  const posterUrl = video?.image?.isExtern ? normalizeBaseUrl(video.image.baseUrl) : optimizedImg;

  const videoSourceUrl = getVideoSourceUrl(video?.fields?.encoding);
  const videoMimeType = getVideoMimeType(video?.fields?.encoding);

  const videoTitle = typeof video.fields.title === 'string' ? video.fields.title : '';

  return (
    <div className={styles.root} style={{ aspectRatio: '16 / 9' }} data-testid="test-video-container">
      <video
        className={styles.ssrVideo}
        playsInline
        preload="metadata"
        width={1200}
        height={675}
        poster={posterUrl}
        aria-label={videoTitle}
      >
        {videoSourceUrl && <source src={videoSourceUrl} type={videoMimeType} />}
      </video>

      <FoundationPlayerClient {...props} />
    </div>
  );
};

export default FoundationPlayer;
